import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { MarkdownContent } from "@repo/ui/markdown/markdownContent";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconKey,
} from "@tabler/icons-react";
import { type FC, useState } from "react";
import { useVoiceAgentSigningKeyQuery } from "../../../api/entities/tool.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

const signatureVerificationInstructions = `## Alpharun Request Signature Verification

Alpharun signs all requests to your custom tool endpoints using HMAC-SHA256. This allows you to verify that requests are genuinely from Alpharun and haven't been tampered with.

### Headers

Each request includes two signature-related headers:

- \`x-alpharun-timestamp\`: Unix timestamp (seconds) when the request was signed
- \`x-alpharun-signature\`: HMAC-SHA256 signature in format \`sha256=<hex_signature>\`

### How the Signature is Generated

1. **Build the string to sign**: \`<timestamp>.<query_string>.<request_body>\`
   - \`timestamp\`: The value from the \`x-alpharun-timestamp\` header
   - \`query_string\`: The URL raw query string (e.g., \`api_key=xyz&version=v1\`)
   - \`request_body\`: The un-parsed raw body string (empty string for GET requests)
   - If the endpoint URL uses path placeholders, the request path is signed as well, so the string to sign is \`<timestamp>.<path>.<query_string>.<request_body>\` — where \`path\` is the request path with the placeholders filled in (e.g., \`/customers/AC-8891/orders\`)

2. **Generate signature**: HMAC-SHA256 of the string to sign using your signing key

### Verification Steps

1. Extract \`x-alpharun-timestamp\` and \`x-alpharun-signature\` headers
2. Reject requests older than 5 minutes (to prevent replay attacks)
3. Reconstruct the string to sign from the request
4. Compute the expected signature
5. Compare signatures using timing-safe comparison`;

export const SigningKeyDisplay: FC = () => {
  const dispatch = useAppDispatch();
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);
  const {
    data: signingKey,
    isLoading,
    isError,
  } = useVoiceAgentSigningKeyQuery();

  const handleCopy = async () => {
    if (signingKey) {
      await navigator.clipboard.writeText(signingKey);
      showNotification(dispatch, {
        id: `signing-key-copy-${Date.now()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Signing key copied",
        secondaryMessage: "The signing key has been copied to your clipboard.",
      });
    }
  };

  const maskedKey = "••••••••••••••••••••••••••••••••";

  return (
    <div className="rounded-lg border border-border-light bg-gray-50 p-4">
      <div className="flex items-center gap-2">
        <IconKey className="h-4 w-4 text-muted-foreground" />
        <Label size={Size.SMALL}>Signing key</Label>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Use this key to verify request signatures from Alpharun.
      </p>
      <div className="mt-2 flex items-center gap-2">
        {isLoading ? (
          <span className="font-mono text-sm text-gray-400">Loading...</span>
        ) : isError ? (
          <span className="text-sm text-red-500">Failed to load key</span>
        ) : (
          <>
            <code className="flex-1 rounded-sm bg-gray-100 px-2 py-1 font-mono text-sm text-foreground">
              {isKeyVisible ? signingKey : maskedKey}
            </code>
            <TooltipTrigger>
              <Button
                variant={ButtonVariant.Tertiary}
                size={Size.MEDIUM}
                onClick={() => setIsKeyVisible(!isKeyVisible)}
                className="rounded-sm p-1 text-muted-foreground hover:bg-gray-200 hover:text-gray-700"
              >
                {isKeyVisible ? (
                  <IconEyeOff className="h-4 w-4" />
                ) : (
                  <IconEye className="h-4 w-4" />
                )}
              </Button>
              <SimpleTooltip>
                {isKeyVisible ? "Hide key" : "Show key"}
              </SimpleTooltip>
            </TooltipTrigger>
            <TooltipTrigger>
              <Button
                variant={ButtonVariant.Tertiary}
                size={Size.MEDIUM}
                onClick={handleCopy}
                className="rounded-sm p-1 text-muted-foreground hover:bg-gray-200 hover:text-gray-700"
              >
                <IconCopy className="h-4 w-4" />
              </Button>
              <SimpleTooltip>Copy to clipboard</SimpleTooltip>
            </TooltipTrigger>
          </>
        )}
      </div>

      <Button
        variant={ButtonVariant.Tertiary}
        size={Size.MEDIUM}
        onClick={() => setIsInstructionsExpanded(!isInstructionsExpanded)}
        className="mt-3 text-xs text-gray-600 hover:text-foreground"
        icon={
          isInstructionsExpanded ? (
            <IconChevronUp className="h-3 w-3" />
          ) : (
            <IconChevronDown className="h-3 w-3" />
          )
        }
      >
        How to verify signatures
      </Button>

      {isInstructionsExpanded && (
        <div className="mt-3 rounded-sm border border-border-light bg-white p-3">
          <div className="prose prose-sm max-w-none text-xs">
            <MarkdownContent>
              {signatureVerificationInstructions}
            </MarkdownContent>
          </div>
        </div>
      )}
    </div>
  );
};
